import { SagaIterator } from 'redux-saga';
import { call, cancel, select, takeEvery } from 'redux-saga/effects';
import {
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorschlagsdatenRequestPayload
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  convertToBffLaufzeit,
  getFachschluessel,
  handleLinieFromVorvertrag,
  mapToBffVorvertragsNummer,
  Produktlinie,
  TierartEnum
} from '@ps20-frontend/frontend-models';
import {
  Fachbaustein,
  Feature,
  SpartenType,
  TaasTarifrisikoschluesselTKV_V2,
  TierAntrag,
  Tierart,
  TkvV2DatenAntrag,
  VorschadenArt
} from '@ps20/bff-models';
import dayjs from 'dayjs';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { PS20Logger } from '@ps20-frontend/logging';
import { convertDate, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { IAppState } from '../appState';
import {
  selectSelectedProduktlinie,
  selectShouldDisplayBadgeInclusive,
} from '../selectors/produkt.selector';
import { getBreedValueOrLabel } from './transformer/tkvData.transformer';

export const HEILBEHANDLUNGSSCHUTZ = 'TM000053';
export const HB_UND_VORSORGESCHUTZ = 'TM000054';
export const OPERATIONEN = 'TM000059';
export const TARIFWECHSEL = 'TM000133';

export function* watchVorschlagdatenChangend(): SagaIterator {
  yield takeEvery(GlobalActions.VORSCHLAGDATEN_REQUESTED, safeSaga(vorschlagdatenSaga, App.TKV_V2));
}

export function* vorschlagdatenSaga(action: GlobalAction<VorschlagsdatenRequestPayload>): SagaIterator {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const angebote = action.payload.angebotsIds;

  if (
    !angebote.some(
      angebot => angebot.appId === App.TKV_V2 && angebot.angebotsId === state.angebotsId
    )
  ) {
    return yield cancel();
  }

  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  if (!ausgewaehlteProduktlinie) {
    PS20Logger.error(
      App.TKV_V2,
      new Error(
        `Vorschlagdaten konnten nicht erzeugt werden: keine Produktlinie selektiert (angebotsId=${state.angebotsId})`
      )
    );
    return yield cancel();
  }
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);

  const vorschlagdaten: TkvV2DatenAntrag = {
    spartenType: SpartenType.TIERKRANKEN_V2,
    jahresbeitrag: state.jahresbeitrag,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    savedJahresbeitragMitRabatt: state.savedJahresbeitragMitRabatt,
    gesamtpreisNachZahlweise: mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
      state.vertragsoptionen.zahlungsweise,
      state.gesamtbeitragMitRabatt,
      state.jahresbeitrag,
      App.TKV_V2
    ),
    savedGesamtbeitragMitRabatt: state.savedGesamtbeitragMitRabatt,
    angebotsId: state.angebotsId,
    antragsId: state.antragsnummer,
    sicherungsglaeubiger: [],
    hspLfnr: state.produkt.hspLfnr,
    vorschaedenVorhanden: false,
    vorschaeden: [
      {
        vorschadenArt: VorschadenArt.TKV_HB,
        anzahl: 0,
        betrachtungszeitraum: 5
      },
      {
        vorschadenArt: VorschadenArt.TKV_OP,
        anzahl: 0,
        betrachtungszeitraum: 5
      }
    ],
    tierAntrag: mapTierAntrag(state),
    produkt: {
      beginn: null,
      ablauf: null,
      selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
      produktline: ausgewaehlteProduktlinie.produktName,
      kurztext: ausgewaehlteProduktlinie.kurztext,
      langtext: ausgewaehlteProduktlinie.langtext,
      fachschluessel: setFachschluessel(state, ausgewaehlteProduktlinie)
    },
    rbdMax: ausgewaehlteProduktlinie.rbdMax,
    rbdAktuell: state.taasInfo.rbdNachlass,
    treuebonus: state.taasInfo.tbNachlass,
    savedRbdNachlass: state.taasInfo.savedRbdNachlass,
    savedTbNachlass: state.taasInfo.savedTbNachlass,
    vorteilsrabatt: state.taasInfo.vorteilsrabatt,
    preisverzeichnis: ausgewaehlteProduktlinie.preisverzeichnis,
    vorversicherung: [],
    antragSpeichernDatum: dayjs().format(DATEFORMAT_SHORT_ISO),
    laufzeit: convertToBffLaufzeit(state.vertragsoptionen.vertragslaufzeit),
    rabattstufe: state.rabattstufe,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
    vorvertragsnummern: mapToBffVorvertragsNummer(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    zielmarktAbweichung: false,
    kooperation: state.vertragsoptionen.selectedKooperation
  };

  handleLinieFromVorvertrag(vorschlagdaten, ausgewaehlteProduktlinie, state.vertragsoptionen);
  yield call(globalPut.put, new GlobalActions().vorschlagsdatenGenerated(vorschlagdaten));
}

export const setFachschluessel = (
  state: IAppState,
  ausgewaehlteProduktlinie: Produktlinie
): Array<Fachbaustein> => {
  const shouldRemoveTwBaustein = selectShouldDisplayBadgeInclusive(state);
  let fachschluessel = getFachschluessel(state.produkt);

  if (shouldRemoveTwBaustein) {
    fachschluessel = fachschluessel.filter(fschl => fschl.fachschluessel !== TARIFWECHSEL);
  }

  fachschluessel.forEach(fschl => {
    switch (fschl.fachschluessel) {
      case TARIFWECHSEL: {
        if (!shouldRemoveTwBaustein) {
          const twZusatzoption = ausgewaehlteProduktlinie.zusatzoptionen.find(
            zusatzoption => zusatzoption.name === TaasTarifrisikoschluesselTKV_V2.TW,
          );
          fschl.deltafaktor = twZusatzoption.deltafaktor;
          fschl.plzFaktor = twZusatzoption.plzFaktor;
        }
        break;
      }
      case OPERATIONEN:
      case HB_UND_VORSORGESCHUTZ:
      case HEILBEHANDLUNGSSCHUTZ: {
        const opGefahr = ausgewaehlteProduktlinie.gefahren.find(
          gefahr =>
            gefahr.name ===
            (fschl.fachschluessel === OPERATIONEN
              ? TaasTarifrisikoschluesselTKV_V2.OP
              : TaasTarifrisikoschluesselTKV_V2.HB),
        );
        fschl.deltafaktor = opGefahr.deltafaktor;
        fschl.anzahlVorschaeden = opGefahr.anzahlVorschaeden;
        fschl.plzFaktor = opGefahr.plzFaktor;
        break;
      }
    }
  });

  return fachschluessel;
};

function convertAnimalTypeToTierart(animalType: TierartEnum): Tierart {
  switch (animalType) {
    case TierartEnum.HUND:
      return Tierart.VRHUND;
    case TierartEnum.KATZE:
      return Tierart.VRKATZE;
    case TierartEnum.PFERD:
      return Tierart.VRPFERD;
    default:
      return null;
  }
}

export function mapTierAntrag(state: IAppState): TierAntrag {
  const versicherungsnehmer = state.versicherungsnehmer;

  const tierAntrag: TierAntrag = {
    geschlecht: versicherungsnehmer.animalGender,
    geburtsdatum: convertDate(versicherungsnehmer.animalBirthDate),
    tierart: convertAnimalTypeToTierart(versicherungsnehmer.animalType),
    rasse: getBreedValueOrLabel(versicherungsnehmer, 'value'),
    tiername: versicherungsnehmer.animalName,
    tierhaltung:
      versicherungsnehmer.animalType === TierartEnum.KATZE
        ? versicherungsnehmer.catKeepingType
        : null
  };

  if (versicherungsnehmer.selectedIllnesses && versicherungsnehmer.selectedIllnesses.length > 0) {
    tierAntrag.diagnosenAntrag = versicherungsnehmer.selectedIllnesses.map(illness => ({
      diagnose: illness.code
    }));
  }

  return tierAntrag;
}
